import { memoizeSelector } from "@/redux/helpers/memoizeSelector";

import { companySelector, userSelector } from "../customers/customers.selectors";

import * as calendarSelectors from "./calendar/calendar.selectors";
import { DEFAULT_COLOUR_BY } from "./colourBy";
import { PLANNER_DISPLAY_FIELDS } from "./displayFields";
import * as orderSelectors from "./orders/orders.selectors";
import { MODULE } from "./planner.actionTypes";

export const isPageLoading = (state) =>
  (orderSelectors.isFetchingOrders(state) && orderSelectors.getOrders(state).length === 0) ||
  calendarSelectors.isFetchingUsers(state);

export const getActiveView = (state) => state.getIn([MODULE, "activeView"]);

export const getActiveOrder = (state) => state.getIn([MODULE, "activeOrder"]);

export const isAssignUserMenuVisible = (state) => state.getIn([MODULE, "isAssignUserMenuVisible"]);
export const getContextMenuOrderUid = (state) => state.getIn([MODULE, "contextMenuOrderUid"]);

export const isAssignLabelMenuVisible = (state) =>
  state.getIn([MODULE, "isAssignLabelMenuVisible"]);

export const getAssignUserDropdownCoordinates = (state) =>
  state.getIn([MODULE, "assignUserDropdownCoordinates"]);

export const getAssignLabelDropdownCoordinates = (state) =>
  state.getIn([MODULE, "assignLabelDropdownCoordinates"]);

export const isOrderModalOpen = (state) => state.getIn([MODULE, "isOrderModalOpen"]);

export const isOrderColumnCollapse = (state) => state.getIn([MODULE, "isOrderColumnCollapse"]);

export const isSettingsModalOpen = (state) => state.getIn([MODULE, "isSettingsModalOpen"]);

export const getSettings = (state) => state.getIn([MODULE, "settings"]);

// The company setting stores work hours as a 24-hour clock string ("09:00:00"), but the Work Hours
// control works in minutes from midnight. Convert here so minutes is the only unit anywhere past
// this point. Values already in minutes (a transient edit) pass straight through.
const toMinutesFromMidnight = (time) => {
  if (typeof time === "number") {
    return time;
  }

  const match = /^(\d{1,2}):(\d{2})/.exec(time ?? "");

  if (!match) {
    return null;
  }

  return Number(match[1]) * 60 + Number(match[2]);
};

export const getPlannerWorkingHours = (state) => {
  const settings = getSettings(state) ?? {};
  const activeCustomer = companySelector(state);

  const plannerWorkHours = activeCustomer?.settings?.plannerSettings?.find(
    (s) => s.key === "PLANNER_WORK_HOURS"
  );

  if (!plannerWorkHours && settings.startTime == null && settings.endTime == null) {
    return null;
  }

  // Each end of the window falls back on its own. Editing only the "From" dropdown leaves "To"
  // untouched, so an all-or-nothing fallback would throw the half-edited window away and the
  // control would look dead. Test against null rather than truthiness: midnight is 0 minutes,
  // which is falsy but a perfectly valid time.
  const pick = (edited, saved) =>
    edited != null ? toMinutesFromMidnight(edited) : toMinutesFromMidnight(saved);

  return {
    ...settings,
    startTime: pick(settings.startTime, plannerWorkHours?.value?.startTime),
    endTime: pick(settings.endTime, plannerWorkHours?.value?.endTime),
  };
};

export const getPlannerDisplayOptions = memoizeSelector([getSettings], (settings) => {
  // Transient in-session working copy, populated the moment a toggle is flipped. It is also the
  // unsaved-changes buffer: a Display change shows on the board straight away and is only written
  // into a view when the user explicitly saves one.
  if (settings.displayOptions?.length > 0) {
    return settings.displayOptions;
  }

  // Nothing chosen yet, so every field is on. A saved view fills the working copy above through
  // applyDisplayBackendKeys, which converts the view's backendKey list into these option objects.
  // Labels are excluded here: they are always shown, not a toggle.
  return PLANNER_DISPLAY_FIELDS.map((field) => ({
    key: field.key,
    title: field.title,
    isSelected: true,
  }));
});

// The board's colour source, owned by the selected view rather than by the user. A view with no
// stored colour, and the default board, both read as "off".
export const getPlannerColourBy = (state) => state.getIn([MODULE, "colourBy"]) ?? DEFAULT_COLOUR_BY;

// Show only when the flag is strictly `false`. It means "has seen", so `undefined` (loading), `null`,
// and `true` must not show; a truthiness check would show the modal to everyone on every visit.
export const shouldShowPlannerWhatsNewSelector = (state) =>
  userSelector(state)?.hasSeenPlanner === false;

export const isCustomAssignUserMenuVisible = (state) =>
  state.getIn([MODULE, "isCustomAssignUserMenuVisible"]);

export const isCustomAssignLabelMenuVisible = (state) =>
  state.getIn([MODULE, "isCustomAssignLabelMenuVisible"]);

export const getFetchCustomEventsStatus = (state) =>
  state.getIn([MODULE, "fetchCustomEventsStatus"]);

export const getFetchCustomEventStatus = (state) => state.getIn([MODULE, "fetchCustomEventStatus"]);

export const getDuplicateEventStatus = (state) => state.getIn([MODULE, "duplicateEventStatus"]);

export const getDeleteCustomEventInstanceStatus = (state) =>
  state.getIn([MODULE, "deleteCustomEventInstanceStatus"]);
export const getDeleteThisCustomEventStatus = (state) =>
  state.getIn([MODULE, "deleteThisCustomEventStatus"]);
export const getDeleteThisAndFollowingCustomEventStatus = (state) =>
  state.getIn([MODULE, "deleteThisAndFollowingCustomEventStatus"]);
export const getDeleteAllCustomEventStatus = (state) =>
  state.getIn([MODULE, "deleteAllCustomEventStatus"]);

export const getUndoDeleteCustomEventInstanceStatus = (state) =>
  state.getIn([MODULE, "undoDeleteCustomEventInstanceStatus"]);
export const getUndoDeleteThisCustomEventStatus = (state) =>
  state.getIn([MODULE, "undoDeleteThisCustomEventStatus"]);
export const getUndoDeleteThisAndFollowingCustomEventStatus = (state) =>
  state.getIn([MODULE, "undoDeleteThisAndFollowingCustomEventStatus"]);
export const getUndoDeleteAllCustomEventStatus = (state) =>
  state.getIn([MODULE, "undoDeleteAllCustomEventStatus"]);

export const getCustomEventDataToUpdate = (state) =>
  state.getIn([MODULE, "customEventDataToUpdate"]);

export const customEvents = (state) => state.getIn([MODULE, "customEvents"]);
export const customEvent = (state) => state.getIn([MODULE, "customEvent"]);

// The live row for one occurrence. Surfaces opened from a snapshot (the drawer) read this so a
// change made there shows straight away instead of after a reopen. Every unmodified occurrence of a
// repeating event carries the SAME uid, so uid alone always returns the earliest one - the date is
// what makes the row unique. A one-off has no occurrence date on either side, so it still matches.
export const getCustomEventOccurrence = (state, uid, occurrenceDate) =>
  uid
    ? (state.getIn([MODULE, "customEvents"]) || []).find(
        (event) => event.uid === uid && (event.occurrenceDate ?? null) === (occurrenceDate ?? null)
      )
    : undefined;

export const getCustomEvents = (state) => {
  const events = state.getIn([MODULE, "customEvents"]);
  const users = calendarSelectors.getUsers(state);

  return events.map((event) => ({
    ...event,
    isCustom: true,
    start: event.startDatetime,
    startTime: event.startDatetime,
    end: event.endDatetime,
    endTime: event.endDatetime,
    title: event.title,
    labels: event.labels,
    assignees: event.assignees.map((assignee) => ({
      ...assignee,
      color: users.find((u) => u.id === assignee.id)?.color,
    })),
  }));
};

export const getCustomEventsByTeam = (state) => {
  const events = getCustomEvents(state);

  const team = [];

  events.forEach((event) => {
    event.assignees.forEach((u) => {
      team.push({
        ...event,
        resource: u.id,
      });
    });
  });

  return team;
};

export const createCustomEventStatus = (state) => state.getIn([MODULE, "createCustomEventStatus"]);
export const updateCustomEventStatus = (state) => state.getIn([MODULE, "updateCustomEventStatus"]);
export const getCustomEventReminders = (state) => state.getIn([MODULE, "customEventReminders"]);
export const getCustomEventReminder = (state) => state.getIn([MODULE, "customEventReminder"]);

export const getActiveCustomEvent = (state) => state.getIn([MODULE, "activeCustomEvent"]);
